import styled from 'styled-components';

import { Card } from '@components/card';
import { ChatBubble } from '@components/chat-bubble';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useMinimumDelay } from '@hooks/use-minimum-delay';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useCompanyDetails } from 'api/company/hooks';
import { useActionSmsTemplate } from 'api/journeys/hooks';
import { ActionPreviewLiveContent } from 'api/journeys/requests';

import { renderWithVariableChips } from '../shared/variable-chips';

export const usePreviewSmsModal = () => useModal(PreviewSms, { variant: 'REGULAR' });

export type PreviewSmsProps = {
  action: 'journey' | 'traveller';
  shortId: string;
  to: string | string[];
  // Fallback body shown while the backend hybrid render is in flight.
  textMessage: string;
  date?: Date;
  // When set (preview opened from an editor), the message is rendered from this
  // unsaved draft instead of the saved template.
  liveContent?: ActionPreviewLiveContent;
  // Labels of broken (deleted/severed source) variables, so the "To" chip can
  // render red to match the email preview.
  brokenLabels?: string[];
};

export const PreviewSms = (props: PreviewSmsProps) => {
  const { action, shortId, to, textMessage, date, liveContent, brokenLabels } = props;
  const brokenLabelSet = brokenLabels?.length ? new Set(brokenLabels) : undefined;
  const { t } = useScopedTranslate(locale);
  const companyQuery = useCompanyDetails();
  const { data: hybridMessage, isLoading } = useActionSmsTemplate(action, shortId, liveContent);
  // Floor the spinner so a fast response doesn't flash it (and hides the raw
  // fallback, whose variables show as ids); `?? isLoading` covers the null tick.
  const isPreviewLoading = useMinimumDelay(isLoading, 500) ?? isLoading;
  const message = hybridMessage ?? textMessage;

  const hasRecipient = Array.isArray(to) ? to.length > 0 : Boolean(to);
  const recipient = Array.isArray(to) ? (to.length > 0 ? to.join(', ') : t('noRecipient')) : to;
  const textDate = date ? abbreviateDate(date, 'en', 'weekday-datetime') : t('date');

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <Stack space={1}>
            <Stack space={4} orientation="horizontal">
              <StyledText text={t('from')} font="semibold14" color="textSecondary" />
              <Text text={companyQuery.data?.name} font="regular14" />
            </Stack>
            <Separator />
            <Stack space={4} orientation="horizontal">
              <StyledText text={t('to')} font="semibold14" color="textSecondary" />
              <Text
                text={renderWithVariableChips(recipient, brokenLabelSet)}
                font="regular14"
                color={hasRecipient ? undefined : 'textHelper'}
              />
            </Stack>
            <Separator />
          </Stack>
          <StyledCard space={2}>
            <Stack space={2}>
              <Stack align="center">
                <Text text={t('textmessage')} font="regular14" color="textSecondary" />
                <Text text={textDate} font="regular14" color="textSecondary" />
              </Stack>
              <Stack orientation="horizontal" align="flex-start">
                {isPreviewLoading ? (
                  <LoadingContainer>
                    <SizedRelativeSpinner size={32} />
                  </LoadingContainer>
                ) : message ? (
                  <ChatBubble color="green100" direction="left" text={message} />
                ) : (
                  <Text color="textHelper" text={t('noContent')} />
                )}
              </Stack>
            </Stack>
          </StyledCard>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const StyledCard = styled(Card)({
  padding: pxToRem(24),
});

const LoadingContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  padding: pxToRem(32),
});

const StyledText = styled(Text)({
  width: pxToRem(80),
});

const locale = {
  en: {
    title: 'SMS preview',
    from: 'From',
    to: 'To',
    textmessage: 'Text message',
    date: 'Day, Date at Time',
    noRecipient: 'Not set',
    noContent: 'SMS has no content',
  },
};
